import { useCallback, useEffect, useState } from 'react';
import {
  Tile,
  Tag,
  Button,
  TextInput,
  Select,
  SelectItem,
  InlineNotification,
  Loading,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Stack,
} from '@carbon/react';
import { Email, Renew, UserFollow, TrashCan } from '@carbon/icons-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_LABELS, ROLES } from '@/lib/roles';
import {
  fetchTeam,
  inviteTeamMember,
  updateTeamMemberRole,
  revokeTeamInvitation,
} from '@/lib/teamApi';
import { getFriendlyApiError } from '@/lib/publicError';

const userHeaders = [
  { key: 'name', header: 'Name' },
  { key: 'email', header: 'Email' },
  { key: 'role', header: 'Role' },
  { key: 'actions', header: 'Actions' },
];

const inviteHeaders = [
  { key: 'email', header: 'Email' },
  { key: 'role', header: 'Role' },
  { key: 'status', header: 'Status' },
  { key: 'actions', header: 'Actions' },
];

const TeamAdminPage = () => {
  const { user: currentUser, getToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(ROLES.MEMBER);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchTeam(getToken);
      setUsers(data.users || []);
      setInvitations(data.invitations || []);
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not load team members. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!inviteEmail.trim()) return;

    setSubmitting(true);
    setError('');
    setNotice(null);

    try {
      const trimmedEmail = inviteEmail.trim();
      await inviteTeamMember({ email: trimmedEmail, role: inviteRole }, getToken);
      setInviteEmail('');
      setInviteRole(ROLES.MEMBER);
      setNotice({
        kind: 'success',
        title: 'Invitation sent',
        subtitle: `An invite was sent to ${trimmedEmail}.`,
      });
      await loadTeam();
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not send that invitation. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    setSubmitting(true);
    setError('');
    setNotice(null);

    try {
      await updateTeamMemberRole(userId, role, getToken);
      setNotice({
        kind: 'success',
        title: 'Role updated',
        subtitle: `User role changed to ${ROLE_LABELS[role] || role}.`,
      });
      await loadTeam();
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not update that role. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    setSubmitting(true);
    setError('');
    setNotice(null);

    try {
      await revokeTeamInvitation(invitationId, getToken);
      setNotice({
        kind: 'success',
        title: 'Invitation revoked',
        subtitle: 'The pending invitation was cancelled.',
      });
      await loadTeam();
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not revoke that invitation. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const userRows = users.map((member) => ({
    id: member.id,
    name: member.name,
    email: member.email,
    role: member.role,
    actions: member.id,
  }));

  const inviteRows = invitations.map((invitation) => ({
    id: invitation.id,
    email: invitation.email,
    role: invitation.role,
    status: invitation.status,
    actions: invitation.id,
  }));

  return (
    <div className="app-page team-admin-page" data-testid="team-admin-page">
      <div className="section-header team-admin-header">
        <div>
          <Tag type="blue">ADMIN</Tag>
          <h2 className="section-title">Team access</h2>
          <p className="section-description">
            Invite people to SenseStack and control who can use admin tools like the contact inbox.
          </p>
        </div>
        <Button kind="ghost" renderIcon={Renew} onClick={loadTeam} disabled={loading || submitting}>
          Refresh
        </Button>
      </div>

      {notice && (
        <InlineNotification
          kind={notice.kind}
          title={notice.title}
          subtitle={notice.subtitle}
          onClose={() => setNotice(null)}
          className="team-admin-notice"
        />
      )}

      {error && (
        <InlineNotification
          kind="error"
          title="Something went wrong"
          subtitle={error}
          onClose={() => setError('')}
          className="team-admin-notice"
        />
      )}

      <Tile className="team-admin-invite">
        <h3 className="team-admin-card__title">Invite someone</h3>
        <form className="team-admin-invite__form" onSubmit={handleInvite}>
          <Stack gap={6} orientation="horizontal" className="team-admin-invite__fields">
            <TextInput
              id="team-invite-email"
              labelText="Email"
              type="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              required
            />
            <Select
              id="team-invite-role"
              labelText="Workspace role"
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value)}
            >
              <SelectItem value={ROLES.MEMBER} text={ROLE_LABELS.member} />
              <SelectItem value={ROLES.ADMIN} text={ROLE_LABELS.admin} />
            </Select>
            <Button type="submit" renderIcon={UserFollow} disabled={submitting || !inviteEmail.trim()}>
              {submitting ? 'Sending…' : 'Send invite'}
            </Button>
          </Stack>
        </form>
        <p className="team-admin-help">
          <strong>Admin</strong> can manage team access and the contact inbox.
          <strong> Member</strong> can use contracts, invoices, and settings.
        </p>
      </Tile>

      {loading ? (
        <div className="team-admin-loading">
          <Loading withOverlay={false} description="Loading team…" />
        </div>
      ) : (
        <>
          <Tile className="team-admin-card">
            <h3 className="team-admin-card__title">Active users ({users.length})</h3>
            {users.length === 0 ? (
              <p className="team-admin-empty">No users yet.</p>
            ) : (
              <DataTable rows={userRows} headers={userHeaders} size="sm">
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <Table {...getTableProps()} size="sm">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const member = users.find((item) => item.id === row.id);

                        return (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            {row.cells.map((cell) => (
                              <TableCell key={cell.id}>
                                {cell.info.header === 'role' && (
                                  <Select
                                    id={`role-${row.id}`}
                                    labelText="Role"
                                    hideLabel
                                    size="sm"
                                    value={member?.role || ROLES.MEMBER}
                                    disabled={submitting || member?.id === currentUser?.id}
                                    onChange={(event) =>
                                      handleRoleChange(row.id, event.target.value)
                                    }
                                  >
                                    <SelectItem value={ROLES.MEMBER} text={ROLE_LABELS.member} />
                                    <SelectItem value={ROLES.ADMIN} text={ROLE_LABELS.admin} />
                                  </Select>
                                )}
                                {cell.info.header === 'actions' && member?.id === currentUser?.id && (
                                  <Tag type="gray" size="sm">You</Tag>
                                )}
                                {!['role', 'actions'].includes(cell.info.header) && cell.value}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            )}
          </Tile>

          <Tile className="team-admin-card">
            <h3 className="team-admin-card__title">Pending invitations ({invitations.length})</h3>
            {invitations.length === 0 ? (
              <p className="team-admin-empty">
                <Email size={20} /> No pending invitations.
              </p>
            ) : (
              <DataTable rows={inviteRows} headers={inviteHeaders} size="sm">
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <Table {...getTableProps()} size="sm">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>
                              {cell.info.header === 'role' && (
                                <Tag type={cell.value === ROLES.ADMIN ? 'blue' : 'gray'} size="sm">
                                  {ROLE_LABELS[cell.value] || cell.value}
                                </Tag>
                              )}
                              {cell.info.header === 'status' && (
                                <Tag type="purple" size="sm">{cell.value}</Tag>
                              )}
                              {cell.info.header === 'actions' && (
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={TrashCan}
                                  disabled={submitting}
                                  onClick={() => handleRevokeInvite(row.id)}
                                >
                                  Revoke
                                </Button>
                              )}
                              {!['role', 'status', 'actions'].includes(cell.info.header) && cell.value}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            )}
          </Tile>
        </>
      )}
    </div>
  );
};

export default TeamAdminPage;
